import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Lock, Unlock, KeyRound, X, ShieldCheck, AlertCircle,
} from "lucide-react";

/**
 * BackOfficeButton — visible (but discreet) pill at the bottom-center of every page.
 *
 * Opens a 3-step gate:
 *  1. Confirm admin email is steveblast@gmail.com (must already be signed in as this user)
 *  2. Enter the back-office PIN (3 digits)
 *  3. On success → unlocks admin chrome AND navigates to /admin (the Back Office hub)
 *
 * Replaces the older invisible KeyRound dot from AdminPinPad. All admin tools
 * (Banners, Coupons, SEO, Integrations vault, Colors/Fonts, Promotions) now live
 * behind this single entry point.
 */
const ADMIN_EMAILS = ["steveblast@gmail.com"];

export default function BackOfficeButton() {
  const { unlocked, unlock, lock, err } = useAdmin();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  // Per Steve's UX: anyone signed in can attempt the PIN. The PIN itself
  // is the gate. (Sensitive backend actions still verify the admin email
  // server-side.)
  const canAttempt = !!user?.user_id;

  const submit = async (e) => {
    e.preventDefault();
    if (!canAttempt) return;
    setBusy(true);
    const ok = await unlock(pin);
    setBusy(false);
    if (ok) {
      setOpen(false);
      setPin("");
      navigate("/admin");
    }
  };

  return (
    <div
      className="fixed z-50 flex flex-col items-start gap-2"
      style={{ bottom: "1rem", left: "1rem" }}
      data-testid="back-office-root"
    >
      {open && (
        <div
          className="bd-surface p-5 mb-2"
          style={{
            boxShadow: "0 20px 60px -12px rgba(0,0,0,0.45)",
            borderRadius: "var(--radius-card)",
            // Stay inside the viewport on narrow phones — the parent
            // pill sits at left:1rem, so the inner panel widens until
            // it would touch the right edge, then stops.
            width: "min(20rem, calc(100vw - 2rem))",
            background: "var(--bg-elev)",
          }}
          data-testid="back-office-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-bold">
              {unlocked
                ? <><Unlock size={16} style={{ color: "var(--primary)" }} /> Admin unlocked</>
                : <><Lock size={16} /> Admin</>
              }
            </div>
            <button
              onClick={() => { setOpen(false); setPin(""); }}
              className="bd-btn bd-btn-ghost"
              style={{ padding: "0.25rem" }}
              data-testid="back-office-close"
            >
              <X size={14} />
            </button>
          </div>

          {!user?.user_id && (
            <div className="text-sm flex items-start gap-2 p-3 mb-2"
                 style={{
                   background: "var(--bg-soft)",
                   border: "1px solid var(--border)",
                   borderRadius: "var(--radius-btn)",
                 }}
                 data-testid="back-office-not-signed-in">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <div>
                Sign in to any Born Day account first, then enter the Admin passcode here.
              </div>
            </div>
          )}

          {!unlocked && canAttempt ? (
            <form onSubmit={submit} className="space-y-2">
              <label className="text-xs uppercase tracking-wider"
                     style={{ color: "var(--text-dim)" }}>
                Admin passcode
              </label>
              <input
                className="bd-input bd-mono text-center text-lg"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                data-testid="back-office-pin-input"
                placeholder="• • •"
              />
              {err && (
                <div className="text-xs" style={{ color: "#ff3b30" }} data-testid="back-office-pin-error">
                  {err}
                </div>
              )}
              <button className="bd-btn w-full" disabled={busy} data-testid="back-office-pin-submit">
                {busy ? "…" : "Unlock Admin"}
              </button>
              <p className="text-[10px] mt-2" style={{ color: "var(--text-dim)" }}>
                The passcode unlocks app-wide admin controls. Only share it with the platform owner.
              </p>
            </form>
          ) : unlocked ? (
            <div>
              <p className="text-xs mb-3" style={{ color: "var(--text-dim)" }}>
                You can edit banners, coupons, SEO, integrations, colors, fonts, and promotions across the app.
              </p>
              <div className="flex gap-2">
                <button
                  className="bd-btn flex-1"
                  onClick={() => { navigate("/admin"); setOpen(false); }}
                  data-testid="back-office-open"
                >
                  Open Admin
                </button>
                <button
                  className="bd-btn bd-btn-ghost"
                  onClick={() => { lock(); setOpen(false); }}
                  data-testid="back-office-lock"
                >
                  Lock
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="bd-btn"
        style={{
          padding: "0.2rem 0.45rem",
          fontSize: "0.62rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          background: unlocked ? "var(--primary)" : "var(--surface)",
          color: unlocked ? "var(--primary-fg)" : "var(--text)",
          border: "1px solid var(--border)",
          boxShadow: unlocked
            ? "0 4px 12px rgba(255, 122, 89, 0.35)"
            : "0 2px 6px rgba(0,0,0,0.08)",
          opacity: 0.85,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.85")}
        aria-label="Admin"
        title="Admin (back office)"
        data-testid="back-office-trigger"
      >
        {unlocked
          ? <><ShieldCheck size={9} /> Admin</>
          : <><KeyRound size={9} /> Admin</>
        }
      </button>
    </div>
  );
}
